import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import { TextStyle, FontSize } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-text-style/color';
import { TableKit } from '@tiptap/extension-table/kit';
import { Box, IconButton, Divider, Popover, CircularProgress, Menu, MenuItem, Tooltip } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatIndentDecrease,
  FormatIndentIncrease,
  Link as LinkIcon,
  LinkOff,
  Title,
  FormatColorText,
  FormatColorReset,
  Image as ImageIcon,
  BorderAll,
  KeyboardArrowUp,
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  HighlightOff,
  ViewColumn,
  MergeType,
  CallSplit,
  FormatColorFill,
  TableRowsOutlined,
  DeleteSweep
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { TableCellWithBg, TableHeaderWithBg } from './tiptap/tableCellWithBackground';
import { richTextTableSx } from '../utils/richTextTableSx';

const PRESET_CELL_BG = '#FDE9D9';

const PRESET_COLORS = [
  '#000000', '#333333', '#666666', '#999999', '#cccccc',
  '#ff0000', '#ff6600', '#ffcc00', '#00cc00', '#0066ff',
  '#6600ff', '#cc00cc', '#ff0066', '#ffffff'
];

const MenuBar = ({ editor, simple = false }) => {
  const [colorAnchorEl, setColorAnchorEl] = React.useState(null);
  const [insertTableAnchor, setInsertTableAnchor] = React.useState(null);
  const [cellFillAnchorEl, setCellFillAnchorEl] = React.useState(null);
  const [imageUploading, setImageUploading] = React.useState(false);
  const fileInputRef = React.useRef(null);
  const { API_URL, token } = useAuth();

  if (!editor) {
    return null;
  }

  const isInTable = editor.isActive('table');
  const currentColor = editor.getAttributes('textStyle').color;

  const handleColorClick = (event) => {
    setColorAnchorEl(event.currentTarget);
  };

  const handleColorClose = () => {
    setColorAnchorEl(null);
  };

  const applyColor = (color) => {
    editor.chain().focus().setColor(color).run();
  };

  const unsetColor = () => {
    editor.chain().focus().unsetColor().run();
    handleColorClose();
  };

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const removeLink = () => {
    editor.chain().focus().unsetLink().run();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    e.target.value = '';
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/api/upload/rich-text-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const { public_url } = await response.json();
      editor.chain().focus().setImage({ src: public_url }).run();
    } catch (err) {
      console.error('Image upload failed:', err);
      window.alert(err.message || 'Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const colorPicker = (
    <>
      <IconButton
        size="small"
        onClick={handleColorClick}
        sx={{
          backgroundColor: currentColor ? '#e0e0e0' : 'transparent',
          '&:hover': { backgroundColor: '#e0e0e0' }
        }}
        title="Text color"
      >
        <FormatColorText fontSize="small" sx={{ color: currentColor || 'inherit' }} />
      </IconButton>
      <IconButton
        size="small"
        onClick={unsetColor}
        disabled={!currentColor}
        title="Remove text color"
      >
        <FormatColorReset fontSize="small" />
      </IconButton>
      <Popover
        open={Boolean(colorAnchorEl)}
        anchorEl={colorAnchorEl}
        onClose={handleColorClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
            {PRESET_COLORS.map((color) => (
              <Box
                key={color}
                onClick={() => applyColor(color)}
                sx={{
                  width: 24,
                  height: 24,
                  backgroundColor: color,
                  border: '1px solid #ccc',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 }
                }}
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <input
              type="color"
              value={currentColor || '#000000'}
              onChange={(e) => applyColor(e.target.value)}
              style={{
                width: 32,
                height: 32,
                border: 'none',
                cursor: 'pointer',
                padding: 0
              }}
            />
            <span style={{ fontSize: 12 }}>Custom</span>
          </Box>
        </Box>
      </Popover>
    </>
  );

  const imageAndLinkControls = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleImageUpload}
      />
      <IconButton
        size="small"
        onClick={() => fileInputRef.current?.click()}
        disabled={imageUploading || !token}
        sx={{
          '&:hover': { backgroundColor: '#e0e0e0' }
        }}
        title="Insert image"
      >
        {imageUploading ? (
          <CircularProgress size={20} />
        ) : (
          <ImageIcon fontSize="small" />
        )}
      </IconButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <IconButton
        size="small"
        onClick={addLink}
        sx={{
          backgroundColor: editor.isActive('link') ? '#e0e0e0' : 'transparent',
          '&:hover': { backgroundColor: '#e0e0e0' }
        }}
        title="Add link"
      >
        <LinkIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={removeLink}
        disabled={!editor.isActive('link')}
        title="Remove link"
      >
        <LinkOff fontSize="small" />
      </IconButton>
    </>
  );

  if (simple) {
    return (
      <Box
        sx={{
          borderBottom: '1px solid #ccc',
          backgroundColor: '#f3f3f3',
          padding: '8px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
          alignItems: 'center'
        }}
      >
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBold().run()}
          sx={{
            backgroundColor: editor.isActive('bold') ? '#e0e0e0' : 'transparent',
            '&:hover': { backgroundColor: '#e0e0e0' }
          }}
          title="Bold"
        >
          <FormatBold fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          sx={{
            backgroundColor: editor.isActive('italic') ? '#e0e0e0' : 'transparent',
            '&:hover': { backgroundColor: '#e0e0e0' }
          }}
          title="Italic"
        >
          <FormatItalic fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          sx={{
            backgroundColor: editor.isActive('underline') ? '#e0e0e0' : 'transparent',
            '&:hover': { backgroundColor: '#e0e0e0' }
          }}
          title="Underline"
        >
          <FormatUnderlined fontSize="small" />
        </IconButton>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {colorPicker}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {imageAndLinkControls}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderBottom: '1px solid #ccc',
        backgroundColor: '#f3f3f3',
        padding: '8px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        alignItems: 'center'
      }}
    >
      {/* Headers */}
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        sx={{
          backgroundColor: editor.isActive('heading', { level: 1 }) ? '#e0e0e0' : 'transparent',
          '&:hover': { backgroundColor: '#e0e0e0' }
        }}
        title="Heading 1"
      >
        <Title fontSize="small" />
        <span style={{ fontSize: '10px', marginLeft: '2px' }}>1</span>
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        sx={{
          backgroundColor: editor.isActive('heading', { level: 2 }) ? '#e0e0e0' : 'transparent',
          '&:hover': { backgroundColor: '#e0e0e0' }
        }}
        title="Heading 2"
      >
        <Title fontSize="small" />
        <span style={{ fontSize: '10px', marginLeft: '2px' }}>2</span>
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        sx={{
          backgroundColor: editor.isActive('heading', { level: 3 }) ? '#e0e0e0' : 'transparent',
          '&:hover': { backgroundColor: '#e0e0e0' }
        }}
        title="Heading 3"
      >
        <Title fontSize="small" />
        <span style={{ fontSize: '10px', marginLeft: '2px' }}>3</span>
      </IconButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      
      {/* Font Size Options */}
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().setFontSize('10px').run()}
        sx={{
          backgroundColor: editor.isActive('textStyle', { fontSize: '10px' }) ? '#e0e0e0' : 'transparent',
          '&:hover': { backgroundColor: '#e0e0e0' },
          fontSize: '9px',
          fontWeight: 500
        }}
        title="Extra small text"
      >
        XS
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().setFontSize('12px').run()}
        sx={{
          backgroundColor: editor.isActive('textStyle', { fontSize: '12px' }) ? '#e0e0e0' : 'transparent',
          '&:hover': { backgroundColor: '#e0e0e0' },
          fontSize: '11px',
          fontWeight: 500
        }}
        title="Small text"
      >
        S
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().unsetFontSize().run()}
        sx={{
          backgroundColor: !editor.getAttributes('textStyle').fontSize ? '#e0e0e0' : 'transparent',
          '&:hover': { backgroundColor: '#e0e0e0' },
          fontSize: '13px',
          fontWeight: 500
        }}
        title="Normal text"
      >
        N
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().setFontSize('18px').run()}
        sx={{
          backgroundColor: editor.isActive('textStyle', { fontSize: '18px' }) ? '#e0e0e0' : 'transparent',
          '&:hover': { backgroundColor: '#e0e0e0' },
          fontSize: '15px',
          fontWeight: 500
        }}
        title="Large text"
      >
        L
      </IconButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Text formatting */}
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleBold().run()}
        sx={{
          backgroundColor: editor.isActive('bold') ? '#e0e0e0' : 'transparent',
          '&:hover': { backgroundColor: '#e0e0e0' }
        }}
        title="Bold"
      >
        <FormatBold fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        sx={{
          backgroundColor: editor.isActive('italic') ? '#e0e0e0' : 'transparent',
          '&:hover': { backgroundColor: '#e0e0e0' }
        }}
        title="Italic"
      >
        <FormatItalic fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        sx={{
          backgroundColor: editor.isActive('underline') ? '#e0e0e0' : 'transparent',
          '&:hover': { backgroundColor: '#e0e0e0' }
        }}
        title="Underline"
      >
        <FormatUnderlined fontSize="small" />
      </IconButton>

      {colorPicker}

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Lists */}
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        sx={{
          backgroundColor: editor.isActive('bulletList') ? '#e0e0e0' : 'transparent',
          '&:hover': { backgroundColor: '#e0e0e0' }
        }}
        title="Bullet list"
      >
        <FormatListBulleted fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        sx={{
          backgroundColor: editor.isActive('orderedList') ? '#e0e0e0' : 'transparent',
          '&:hover': { backgroundColor: '#e0e0e0' }
        }}
        title="Numbered list"
      >
        <FormatListNumbered fontSize="small" />
      </IconButton>

      {/* Indentation */}
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().liftListItem('listItem').run()}
        disabled={!editor.can().liftListItem('listItem')}
        title="Decrease indent"
      >
        <FormatIndentDecrease fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
        disabled={!editor.can().sinkListItem('listItem')}
        title="Increase indent"
      >
        <FormatIndentIncrease fontSize="small" />
      </IconButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Table */}
      <Tooltip title="Insert table">
        <IconButton size="small" onClick={(e) => setInsertTableAnchor(e.currentTarget)}>
          <BorderAll fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={insertTableAnchor}
        open={Boolean(insertTableAnchor)}
        onClose={() => setInsertTableAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <MenuItem
          dense
          onClick={() => {
            editor.chain().focus().insertTable({ rows: 9, cols: 2, withHeaderRow: true }).run();
            setInsertTableAnchor(null);
          }}
        >
          2 columns × 9 rows (with header row)
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            editor.chain().focus().insertTable({ rows: 4, cols: 4, withHeaderRow: true }).run();
            setInsertTableAnchor(null);
          }}
        >
          4 × 4 (with header row)
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: false }).run();
            setInsertTableAnchor(null);
          }}
        >
          3 × 3 (no header row)
        </MenuItem>
      </Menu>
      <Tooltip title="Add row above">
        <span>
          <IconButton
            size="small"
            disabled={!editor.can().addRowBefore()}
            onClick={() => editor.chain().focus().addRowBefore().run()}
          >
            <KeyboardArrowUp fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Add row below">
        <span>
          <IconButton
            size="small"
            disabled={!editor.can().addRowAfter()}
            onClick={() => editor.chain().focus().addRowAfter().run()}
          >
            <KeyboardArrowDown fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Delete row">
        <span>
          <IconButton
            size="small"
            disabled={!editor.can().deleteRow()}
            onClick={() => editor.chain().focus().deleteRow().run()}
          >
            <HighlightOff fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Add column before">
        <span>
          <IconButton
            size="small"
            disabled={!editor.can().addColumnBefore()}
            onClick={() => editor.chain().focus().addColumnBefore().run()}
          >
            <KeyboardArrowLeft fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Add column after">
        <span>
          <IconButton
            size="small"
            disabled={!editor.can().addColumnAfter()}
            onClick={() => editor.chain().focus().addColumnAfter().run()}
          >
            <KeyboardArrowRight fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Delete column">
        <span>
          <IconButton
            size="small"
            disabled={!editor.can().deleteColumn()}
            onClick={() => editor.chain().focus().deleteColumn().run()}
          >
            <ViewColumn fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Toggle header row">
        <span>
          <IconButton
            size="small"
            disabled={!isInTable || !editor.can().toggleHeaderRow()}
            onClick={() => editor.chain().focus().toggleHeaderRow().run()}
          >
            <TableRowsOutlined fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Merge selected cells">
        <span>
          <IconButton
            size="small"
            disabled={!editor.can().mergeCells()}
            onClick={() => editor.chain().focus().mergeCells().run()}
          >
            <MergeType fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Split cell">
        <span>
          <IconButton
            size="small"
            disabled={!editor.can().splitCell()}
            onClick={() => editor.chain().focus().splitCell().run()}
          >
            <CallSplit fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Cell background">
        <span>
          <IconButton
            size="small"
            disabled={!isInTable}
            onClick={(e) => setCellFillAnchorEl(e.currentTarget)}
          >
            <FormatColorFill fontSize="small" sx={{ color: PRESET_CELL_BG }} />
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={Boolean(cellFillAnchorEl)}
        anchorEl={cellFillAnchorEl}
        onClose={() => setCellFillAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box
            component="button"
            type="button"
            onClick={() => {
              editor.chain().focus().setCellAttribute('backgroundColor', PRESET_CELL_BG).run();
              setCellFillAnchorEl(null);
            }}
            sx={{
              width: 28,
              height: 28,
              backgroundColor: PRESET_CELL_BG,
              border: '1px solid #ccc',
              borderRadius: 1,
              cursor: 'pointer',
              p: 0
            }}
            title="Peach tint"
          />
          <Box
            component="button"
            type="button"
            onClick={() => {
              editor.chain().focus().setCellAttribute('backgroundColor', '#ffffff').run();
              setCellFillAnchorEl(null);
            }}
            sx={{
              width: 28,
              height: 28,
              backgroundColor: '#fff',
              border: '1px solid #ccc',
              borderRadius: 1,
              cursor: 'pointer',
              p: 0
            }}
            title="White"
          />
          <Box
            component="button"
            type="button"
            onClick={() => {
              editor.chain().focus().setCellAttribute('backgroundColor', null).run();
              setCellFillAnchorEl(null);
            }}
            sx={{
              border: '1px dashed #bbb',
              background: 'none',
              borderRadius: 1,
              px: 1,
              py: 0.5,
              cursor: 'pointer',
              fontSize: 13
            }}
          >
            Clear fill
          </Box>
        </Box>
      </Popover>
      <Tooltip title="Delete entire table">
        <span>
          <IconButton
            size="small"
            disabled={!editor.can().deleteTable()}
            onClick={() => editor.chain().focus().deleteTable().run()}
          >
            <DeleteSweep fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {imageAndLinkControls}
    </Box>
  );
};

const RichTextEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Start typing...', 
  readOnly = false, 
  style = {},
  error = false,
  /** When true: Bold, Italic, Underline, Color, Image, Link, Unlink only */
  simple = false,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: simple ? false : { levels: [1, 2, 3] },
        bulletList: simple ? false : undefined,
        orderedList: simple ? false : undefined,
        listItem: simple ? false : undefined,
        codeBlock: false,
        code: false,
        blockquote: simple ? false : undefined,
        horizontalRule: simple ? false : undefined,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer'
        }
      }),
      Image.configure({
        inline: true,
        allowBase64: false
      }),
      TextStyle,
      ...(simple
        ? []
        : [
            FontSize.configure({
              types: ['textStyle'],
            }),
          ]),
      Color.configure({
        types: ['textStyle'],
      }),
      ...(simple
        ? []
        : [
            TableKit.configure({
              table: {
                resizable: false,
                renderWrapper: true,
                HTMLAttributes: { class: 'rt-editor-table' },
              },
              tableCell: false,
              tableHeader: false,
            }),
            TableCellWithBg,
            TableHeaderWithBg,
          ]),
    ],
    content: value || '',
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      if (onChange) {
        const html = editor.getHTML();
        onChange(html);
      }
    }
  });

  // Sync external value changes without stealing focus while the user is typing
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') === current) return;
    // Parent state updates from onUpdate would otherwise call setContent and drop focus
    if (editor.isFocused) return;
    editor.commands.setContent(value || '', false);
  }, [value, editor]);

  // Update editable state when readOnly prop changes
  React.useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  return (
    <Box
      sx={{
        border: error ? '1px solid #d32f2f' : '1px solid #c4c4c4',
        borderRadius: '4px',
        backgroundColor: '#fff',
        ...style
      }}
    >
      {!readOnly && <MenuBar editor={editor} simple={simple} />}
      <Box
        sx={{
          ...(simple ? {} : richTextTableSx),
          '& .ProseMirror': {
            minHeight: simple ? '120px' : '150px',
            padding: '12px',
            fontSize: '14px',
            outline: 'none',
            '& p': {
              margin: '0 0 8px 0',
              '&:last-child': {
                marginBottom: 0
              }
            },
            '& h1': {
              fontSize: '2em',
              fontWeight: 'bold',
              margin: '0 0 8px 0'
            },
            '& h2': {
              fontSize: '1.5em',
              fontWeight: 'bold',
              margin: '0 0 8px 0'
            },
            '& h3': {
              fontSize: '1.17em',
              fontWeight: 'bold',
              margin: '0 0 8px 0'
            },
            '& ul, & ol': {
              paddingLeft: '24px',
              margin: '0 0 8px 0',
              '& ul, & ol': {
                margin: '4px 0'
              }
            },
            '& ul': {
              listStyleType: 'disc',
              '& ul': {
                listStyleType: 'circle',
                '& ul': {
                  listStyleType: 'square'
                }
              }
            },
            '& ol': {
              listStyleType: 'decimal',
              '& ol': {
                listStyleType: 'lower-alpha',
                '& ol': {
                  listStyleType: 'lower-roman'
                }
              }
            },
            '& li': {
              marginBottom: '4px'
            },
            '& a': {
              color: '#1976d2',
              textDecoration: 'underline',
              cursor: 'pointer'
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              verticalAlign: 'middle'
            },
            '& .ProseMirror-focused': {
              outline: 'none'
            }
          },
          '& .ProseMirror p.is-editor-empty:first-of-type::before': {
            content: `"${placeholder}"`,
            color: '#adb5bd',
            pointerEvents: 'none',
            height: 0,
            float: 'left'
          }
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default RichTextEditor;

